import { config } from '../config';
import { lockedOrder, type BoardTile } from './order';
import { arrangeBy, holdOrder, reorderAllowed, type QuietGate } from './reorder';
import type { StatusOverride } from '../types';

/**
 * LIVE-1, LIVE-3, LIVE-5, LIVE-12, LOCK-5, LOCK-7, INV-11 — **what the locked board renders**, pure.
 *
 * `order.ts` owns the comparator (INV-11), `board.ts` owns position allocation (INV-4/INV-5), and
 * `reorder.ts` owns DISC-7's quiet gate. Neither of the three is re-derived here. What this module
 * adds is the one thing none of them can express on its own: **the grid**, which is `BOARD_CAP`
 * cells, always, whatever the board holds.
 *
 * ── The grid is padding, never indexing, and that distinction is the unit ────
 *
 * §6.2 is explicit, and it is the sentence this file exists to obey:
 *
 * > The render is packed. Always. `lockedOrder` partitions on `isActive` and then sorts by
 * > `boardPosition`; it **never indexes a grid cell by `boardPosition`**. Because positions are a
 * > contiguous prefix, the packed result *happens to* place each active tile in the grid cell
 * > matching its position — but that is a **consequence** of the allocation rule, not a licence to
 * > implement position-indexed rendering. A position-indexed implementation would place a sunk
 * > tile's hole inside the active region and break LIVE-3.
 *
 * So {@link boardSlots} takes an **id order** and pads it. It never reads `boardPosition` to choose
 * a cell. The two failure modes that buys immunity from are worth naming, because both are silent:
 * a `disproved` tile at position 2 leaving a hole in the middle of the live board (LIVE-3), and a
 * future allocation rule that admits a middle gap turning every later tile into an empty cell.
 *
 * ── LOCK-7's empty slots are `null`, and that is the mechanism, not a style ──
 *
 * > *"Unfilled grid slots render as an explicit empty state and MUST NOT be tappable as
 * > increments."*
 *
 * An empty slot is `null`. A `null` carries no id, so there is nothing for §5.1 step 4 to build an
 * `IncrementCommand` from and nothing for `data-hid` to name — which is what makes *"an empty slot
 * tap creates no Observation"* (§17.1) a property of the type rather than of a component's `if`.
 *
 * ── LIVE-12, restated as what this module cannot do ─────────────────────────
 *
 * `BoardTile` declares no `cachedCount` and no `status`, so **no function in this file can rank by a
 * count**. That is `order.ts`'s parameter-type mechanism reaching one layer up: the board's render
 * order is a function of `(statusOverride, boardPosition)` and the compiler refuses anything else.
 */

/**
 * The number of cells LIVE-1's 2×4 grid draws — `BOARD_CAP`, not a second number (NFR-9).
 *
 * Named rather than spelled at the call site because the grid and the cap are the *same* claim: a
 * ninth cell would be a place to put a ninth tile, and LIVE-8 says no path creates one.
 */
export const BOARD_SLOT_COUNT = config.BOARD_CAP;

/** One grid cell: a tile, or LOCK-7's explicit empty state. */
export type BoardSlot<T> = T | null;

/**
 * INV-11's answer for this board — the ids, in rendered order.
 *
 * Delegates wholly to `lockedOrder`, which is the §16.3 cross-cutting contract for exactly this.
 * The projection to ids is here rather than there because the *gate* works on ids: what DISC-7
 * defers is a change of rendered position, and a position is an index into a list of ids.
 */
export function boardTargetOrder<T extends BoardTile>(tiles: readonly T[]): string[] {
  return lockedOrder(tiles).map((tile) => tile.id);
}

/**
 * The **one** entry point the locked board calls to decide what order to render — on load, after a
 * commit, after a status change, on return from background, and on every quiet-window timeout.
 *
 * Gate open → INV-11's order. Gate shut → {@link holdOrder}, which moves nothing and appends a
 * newly-positioned tile at the tail. One path, so DISC-7's guarantee on this surface is a property
 * of this function rather than of five call sites agreeing — the same shape `nextPageOrders` gives
 * Discovery, because §17.1 asks for the identical guarantee on both.
 *
 * **This is the LIVE-11 and LIVE-5 case, not a hypothetical one.** Tapping a `disproved` tile clears
 * its override and floats it back up among the active tiles; swiping one left sinks it and shifts
 * everything below up. Both are reflows landing on the exact spot a follow-up tap is aimed at, which
 * is why §5.5 extends the deferral to this surface by name.
 */
export function nextBoardOrder<T extends BoardTile>(
  tiles: readonly T[],
  previous: readonly string[],
  gate: QuietGate,
  now: number,
): string[] {
  const target = boardTargetOrder(tiles);
  return reorderAllowed(gate, now) ? target : holdOrder(previous, target);
}

/**
 * LIVE-1 / LOCK-7 — the grid, as exactly `BOARD_SLOT_COUNT` cells.
 *
 * Positioned tiles in the given order, then empty cells to fill the grid. List-only hypotheses
 * (`boardPosition === null`) are **not tiles** and never appear: they live on Discovery pages, which
 * is what INV-4's null means.
 *
 * An over-full board is padded to nothing and is **not truncated**. Silently hiding a ninth tile
 * would make LIVE-8's breach invisible on the one screen where it matters; the caller runs
 * `validateBoard` and surfaces the rejection (NFR-8), and this function's job is to render what is
 * actually there.
 */
export function boardSlots<T extends BoardTile>(
  tiles: readonly T[],
  order: readonly string[],
): BoardSlot<T>[] {
  const positioned = tiles.filter((tile) => tile.boardPosition !== null);
  const arranged: BoardSlot<T>[] = arrangeBy(positioned, order);
  const empty = Math.max(0, BOARD_SLOT_COUNT - arranged.length);
  return [...arranged, ...Array.from<BoardSlot<T>>({ length: empty }).fill(null)];
}

/**
 * LIVE-5 — what a swipe-left leaves on the tile it lands on.
 *
 * > *"Swipe-left sets `disproved`, reversible via the same gesture or the Halftime screen."*
 *
 * A **toggle over the dismissal dimension**, and the two readings it has to reconcile are worth
 * stating because the requirements settle only one of them:
 *
 * - A tile with no dismissal takes `disproved`. That is LIVE-5's first clause.
 * - A dismissed tile is **cleared**, whichever dismissal it carries. LIVE-5's *"reversible via the
 *   same gesture"* settles the `disproved` case directly. The `invalidated` case is not spelled out,
 *   and clearing it is the reading taken here for two reasons: D-12 forbids **setting**
 *   `invalidated` on the in-play board and clearing is not setting; and the alternative — a swipe
 *   that does nothing on an `invalidated` tile — is a silent ignore on a live capture surface, which
 *   is precisely what LIVE-11 refuses for the tap on the same tile. Reported for ratification.
 *
 * The gesture can therefore never *produce* `invalidated`, which is the half D-12 actually protects:
 * the return type admits it only as an input.
 */
export function swipeLeftOverride(current: StatusOverride): StatusOverride {
  return current === null ? 'disproved' : null;
}
